import React, { useState, useEffect } from "react";
import { FiSend, FiZap, FiUsers, FiAlertTriangle, FiRefreshCw } from "react-icons/fi";
import { useSelector } from "react-redux";
import subscriptionRequests from "../../api/Customer/SubscriptionRequests";

const QuickSmsHeader = ({ recipients = [], message = "" }) => {
  const user = useSelector((state) => state.user?.txtconnect_ng_user);
  const [availableUnits, setAvailableUnits] = useState(parseInt(user?.units) || 0);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Function to fetch live units from API
  const fetchLiveUnits = async () => {
    try {
      setLoadingUnits(true);
      const response = await subscriptionRequests.getCurrentUserSubscriptionData();
      
      if (response.status_code === "200" && response.data) {
        // Extract units from API response
        let unitsFromAPI = 0;
        
        if (response.data.data?.units) {
          unitsFromAPI = response.data.data.units;
        } else if (response.data.remaining_units) {
          unitsFromAPI = response.data.remaining_units;
        } else if (response.data.sms_balance) {
          unitsFromAPI = response.data.sms_balance;
        } else if (response.data.units) {
          unitsFromAPI = response.data.units;
        } else if (response.data.data?.sms_balance) {
          unitsFromAPI = response.data.data.sms_balance;
        }
        
        const newUnits = parseInt(unitsFromAPI) || 0;
        setAvailableUnits(newUnits);
        setLastUpdated(new Date());
        
        console.log('Units fetched:', newUnits); // Debug log
        
      } else {
        // Fallback to Redux data if API fails
        setAvailableUnits(parseInt(user?.units) || 0);
      }
    } catch (error) {
      console.error("Error fetching live units:", error);
      // Fallback to Redux data on error
      setAvailableUnits(parseInt(user?.units) || 0);
    } finally {
      setLoadingUnits(false);
    }
  };

  // Fetch units on component mount
  useEffect(() => {
    fetchLiveUnits();
  }, []);

  // Listen for page visibility changes (when user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Refresh units when user returns to the page
        fetchLiveUnits();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Also listen for Redux store updates
  useEffect(() => {
    if (user?.units) {
      const userUnits = parseInt(user.units) || 0;
      if (userUnits !== availableUnits) {
        setAvailableUnits(userUnits);
      }
    }
  }, [user?.units]);

  // Manual refresh function
  const handleRefreshUnits = () => {
    fetchLiveUnits();
  };

  const messageLength = message.length;
  const smsParts = Math.ceil(messageLength / 160) || 1;
  const totalSms = recipients.length * smsParts;
  const hasEnoughUnits = availableUnits >= totalSms;

  // Format last updated time
  const formatLastUpdated = () => {
    if (!lastUpdated) return "Not updated yet";
    const now = new Date();
    const diffMs = now - lastUpdated;
    const diffSec = Math.floor(diffMs / 1000);
    
    if (diffSec < 60) return `${diffSec} seconds ago`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} minutes ago`;
    return `${Math.floor(diffSec / 3600)} hours ago`;
  };

  return (
    <div className="relative p-4 mb-6 bg-[#E5EAFF] border border-[#6C86FF] rounded-2xl md:p-6 lg:p-8">
      <div className="relative z-10">
        {/* Header section */}
        <div className="flex flex-col gap-4 mb-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="p-2 rounded-xl backdrop-blur-sm bg-white/60 md:p-3">
              <FiSend className="text-xl text-[#6C86FF] md:text-2xl" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#595959] md:text-2xl lg:text-3xl">
                Quick SMS
              </h1>
              <p className="text-sm text-[#595959] md:text-base lg:text-lg">
                Send instant messages to up to 10 contacts.
              </p>
            </div>
          </div>

          {/* Feature icons - hidden on mobile, shown on tablet+ */}
          <div className="hidden items-center space-x-4 md:flex lg:space-x-6">
            <div className="text-center">
              <div className="flex justify-center items-center mb-2 w-10 h-10 rounded-full bg-white/60 md:w-12 md:h-12">
                <FiZap className="text-lg text-[#6C86FF] md:text-xl" />
              </div>
              <span className="text-xs font-medium text-[#595959] md:text-sm">Instant</span>
            </div>
            <div className="text-center">
              <div className="flex justify-center items-center mb-2 w-10 h-10 rounded-full bg-white/60 md:w-12 md:h-12">
                <FiUsers className="text-lg text-[#6C86FF] md:text-xl" />
              </div>
              <span className="text-xs font-medium text-[#595959] md:text-sm">Quick Send</span>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="p-3 rounded-xl border border-[#6C86FF]/30 backdrop-blur-sm bg-white/60 md:p-4">
          <div className="grid grid-cols-2 gap-3 text-center sm:grid-cols-4 md:gap-4">
            {/* Recipients */}
            <div className="p-3 rounded-lg bg-white/30 border border-[#6C86FF]/20 md:p-4">
              <div className="text-lg font-bold text-[#6C86FF] md:text-xl lg:text-2xl">
                {recipients.length}
              </div>
              <div className="text-xs text-[#595959] md:text-sm">Recipients</div>
            </div>

            {/* Characters */}
            <div className="p-3 rounded-lg bg-white/30 border border-[#6C86FF]/20 md:p-4">
              <div className="text-lg font-bold text-[#6C86FF] md:text-xl lg:text-2xl">
                {messageLength}
              </div>
              <div className="text-xs text-[#595959] md:text-sm">Characters</div>
            </div>

            {/* SMS Pages */}
            <div className="p-3 rounded-lg bg-white/30 border border-[#6C86FF]/20 md:p-4">
              <div className="text-lg font-bold text-[#6C86FF] md:text-xl lg:text-2xl">
                {smsParts}
              </div>
              <div className="text-xs text-[#595959] md:text-sm">SMS Pages</div>
            </div>

            {/* Live Units with auto-update */}
            <div className="p-3 rounded-lg bg-white/30 border border-[#6C86FF]/20 md:p-4">
              <div className="flex flex-col items-center justify-center">
                <div className="flex items-center gap-2 mb-1">
                  <button
                    onClick={handleRefreshUnits}
                    disabled={loadingUnits}
                    className="p-1 text-[#6C86FF] hover:text-[#4a63d6] transition-colors disabled:opacity-50 relative group"
                    title={`Refresh SMS Units\nLast updated: ${formatLastUpdated()}`}
                  >
                    <FiRefreshCw className={`w-3 h-3 md:w-4 ${loadingUnits ? 'animate-spin' : ''}`} />
                    <span className="absolute -top-2 -right-2 w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                  </button>
                  <div
                    className={`text-lg font-bold md:text-xl lg:text-2xl ${
                      hasEnoughUnits ? "text-[#6C86FF]" : "text-yellow-300"
                    }`}
                  >
                    {loadingUnits ? (
                      <span className="inline-block w-6 h-6 bg-gray-300 rounded-full animate-pulse md:w-8 md:h-8"></span>
                    ) : (
                      availableUnits
                    )}
                  </div>
                </div>
                <div className="text-xs text-[#595959] md:text-sm">Live Units</div>
                {/* <div className="text-[10px] text-gray-400 mt-1">
                  {lastUpdated ? formatLastUpdated() : 'Loading...'}
                </div> */}
              </div>
            </div>
          </div>
        </div>

        {/* Unit Warning */}
        {!hasEnoughUnits && recipients.length > 0 && (
          <div className="p-3 mt-4 rounded-xl border backdrop-blur-sm bg-yellow-500/20 border-yellow-400/30 md:p-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-3 md:items-center">
                <FiAlertTriangle className="text-lg text-yellow-300 flex-shrink-0 mt-0.5 md:text-xl md:mt-0" />
                <div className="space-y-1">
                  <div className="font-semibold text-yellow-200 text-sm md:text-base">
                    Insufficient SMS Units
                  </div>
                  <div className="text-xs text-yellow-100 md:text-sm">
                    You need {totalSms} units but only have {availableUnits} units
                    remaining.
                    {availableUnits > 0
                      ? " Consider reducing recipients or message length."
                      : " Please purchase a subscription plan."}
                  </div>
                </div>
              </div>
              <button
                onClick={handleRefreshUnits}
                disabled={loadingUnits}
                className="px-3 py-2 text-xs font-medium text-yellow-100 bg-yellow-500/30 rounded-lg border border-yellow-400/30 hover:bg-yellow-500/40 transition-colors disabled:opacity-50 w-full md:w-auto md:text-sm"
              >
                {loadingUnits ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickSmsHeader;